import React from "react";

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleClearAndReload = () => {
    // ACHTUNG: NICHT localStorage.clear() — das löscht u.a. den Backup-Cache
    // für Projektdaten (alter Schlüssel `bauwerkplus_projects`). Stattdessen nur Session-Cache leeren.
    try {
      sessionStorage.clear();
    } catch {}
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-6 border border-red-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 text-xl">!</span>
              </div>
              <h1 className="text-xl font-bold text-gray-900">Ein Fehler ist aufgetreten</h1>
            </div>
            <p className="text-gray-700 mb-4">
              Die Seite konnte nicht geladen werden. Sie können die Seite neu laden oder den lokalen Speicher zurücksetzen, falls das Problem bestehen bleibt.
            </p>
            <div className="bg-red-50 border border-red-200 rounded p-3 mb-4 text-xs font-mono text-red-900 whitespace-pre-wrap break-all max-h-64 overflow-auto">
              {this.state.error?.toString() || "Unbekannter Fehler"}
              {this.state.errorInfo?.componentStack && (
                <>
                  {"\n\n"}
                  {this.state.errorInfo.componentStack.split("\n").slice(0, 15).join("\n")}
                </>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={this.handleReload}
                className="px-4 py-2 bg-bauwerk-orange text-white rounded hover:opacity-90 transition-opacity"
                data-testid="error-boundary-reload"
              >
                Seite neu laden
              </button>
              <button
                onClick={this.handleClearAndReload}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                data-testid="error-boundary-clear"
              >
                Cache leeren & neu laden
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
